import React from "react";
import { appHistory } from "@/router/history";
import { withTranslation, type WithTranslation } from "react-i18next";
import {
  useDeleteNotification,
  useDeleteNotifications,
  useMarkNotificationAsRead,
  useMarkNotificationsAsRead,
  useNotifications,
} from "api/queries/useNotifications";

import { isAllowed } from "helper/acl";
import { safeRedirect } from "helper/safeRedirect";
import { buildBreadcrumbs } from "helper/breadcrumbs";
import PageHeader from "../Common/PageHeader";
import ListPager from "../Common/ListPager";
import NotificationList, { type NotificationCardItem } from "./NotificationList";
import Loader from "../Common/Loader";
import { Button } from "../ui";
import { withAuthData, type AuthInjectedProps } from "../../context/AuthContext";

const ListPagerAny = ListPager as unknown as React.ComponentType<Record<string, unknown>>;
const NotificationListAny = NotificationList as unknown as React.ComponentType<
  Record<string, unknown>
>;

interface NotificationPageOwnProps extends WithTranslation {}

interface NotificationPageStateProps {
  notifications: NotificationCardItem[];
  notificationsLoaded: boolean;
  page: number;
  pageSize: number;
  user_id: number | string | null;
}

interface NotificationPageDispatchProps {
  changePage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  markNotificationAsRead: (notificationId: number | string) => Promise<number | string | null>;
  deleteNotification: (notificationId: number | string) => Promise<number | string | null>;
  markNotificationsAsRead: (
    notificationIds: Array<number | string>
  ) => Promise<Array<number | string>>;
  deleteNotifications: (notificationIds: Array<number | string>) => Promise<Array<number | string>>;
}

type NotificationPageProps = NotificationPageOwnProps &
  NotificationPageStateProps &
  NotificationPageDispatchProps;

function normalizeTargetPath(targetPath: string | undefined): string | null {
  const path = String(targetPath || "").trim();
  if (!path) {
    return null;
  }
  if (/^https?:\/\//i.test(path) || path.startsWith("//")) {
    return null;
  }

  const relativePath = path.indexOf(BASE_PATH) === 0 ? path.slice(BASE_PATH.length) : path;
  const legacyDocumentTarget = relativePath.match(
    /^\/?modules\/([^/]+)\/([^/]+)\/document\/([^/?#]+)$/
  );
  if (legacyDocumentTarget) {
    const [, parentSlug, moduleSlug, documentId] = legacyDocumentTarget;
    return `${BASE_PATH}/modules/${parentSlug}/${moduleSlug}#document-${documentId}`;
  }

  if (path.indexOf(BASE_PATH) === 0) {
    return path;
  }
  return BASE_PATH + "/" + path.replace(/^\/+/, "");
}

function NotificationPage({
  t,
  notifications,
  notificationsLoaded,
  page,
  pageSize,
  user_id,
  changePage,
  changePageSize,
  markNotificationAsRead,
  deleteNotification,
  markNotificationsAsRead,
  deleteNotifications,
}: NotificationPageProps) {
  const [selectedNotificationIds, setSelectedNotificationIds] = React.useState<string[]>([]);
  const sliceList = (list: NotificationCardItem[]) => {
    return list.slice((page - 1) * pageSize, page * pageSize);
  };

  const handleChangePage = (next: number) => {
    const pageNumber = Math.ceil(notifications.length / pageSize) || 1;
    if (next >= 1 && next <= pageNumber) {
      changePage(next);
    }
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLSelectElement>) => {
    changePageSize(parseInt(event.target.value, 10));
  };

  const handleMarkSelectedAsRead = () => {
    if (user_id == null) return;
    const unreadSelectedIds = notifications
      .filter(
        (notification) =>
          selectedNotificationIds.includes(String(notification.id)) &&
          notification.read !== true &&
          notification.read !== 1 &&
          notification.read !== "1"
      )
      .map((notification) => notification.id);

    if (unreadSelectedIds.length === 0) return;
    markNotificationsAsRead(unreadSelectedIds).then(() => setSelectedNotificationIds([]));
  };

  const handleDeleteSelected = () => {
    if (user_id == null) return;
    const ids = notifications
      .filter((notification) => selectedNotificationIds.includes(String(notification.id)))
      .map((notification) => notification.id);

    if (ids.length === 0) return;
    deleteNotifications(ids).then(() => setSelectedNotificationIds([]));
  };

  const handleSelectionChange = (notificationId: number | string, selected: boolean) => {
    const id = String(notificationId);
    setSelectedNotificationIds((current) =>
      selected ? [...new Set([...current, id])] : current.filter((currentId) => currentId !== id)
    );
  };

  const handleOpenNotification = (notification: NotificationCardItem) => {
    const targetPath = normalizeTargetPath(notification && notification.targetPath);
    if (!targetPath) {
      return;
    }

    const unread =
      notification.read !== true && notification.read !== 1 && notification.read !== "1";
    const navigate = () => {
      if (safeRedirect(targetPath)) {
        return;
      }
      appHistory.push(targetPath);
    };

    if (!unread) {
      navigate();
      return;
    }

    if (user_id == null) {
      navigate();
      return;
    }

    Promise.resolve(markNotificationAsRead(notification.id)).then((result) => {
      if (result !== null) {
        navigate();
      }
    });
  };

  const renderHero = (total: number, unreadCount: number) => {
    const meta = (
      <>
        <div className="PageHero-stat">
          <span className="PageHero-statLabel">{t("notifications.total_label")}</span>
          <strong className="PageHero-statValue">{total}</strong>
        </div>
        <div className="PageHero-stat">
          <span className="PageHero-statLabel">{t("notifications.unread_label")}</span>
          <strong className="PageHero-statValue">{unreadCount}</strong>
        </div>
      </>
    );

    return (
      <PageHeader
        breadcrumbs={buildBreadcrumbs(t, "notification")}
        title={t("notifications.title")}
        meta={meta}
      />
    );
  };

  const breadcrumbs = buildBreadcrumbs(t, "notification");

  if (!isAllowed("webservice.notification")) {
    return (
      <div className="Page NotificationPage">
        <div className="Page-content NotificationPage-content">
          <PageHeader
            breadcrumbs={breadcrumbs}
            kicker="Accès"
            title={t("common.forbiden")}
            className="PageHero--forbidden"
          />
        </div>
      </div>
    );
  }

  const pageNumber = Math.ceil(notifications.length / pageSize) || 1;
  const unreadCount = notifications.filter(
    (notification) =>
      notification.read !== true && notification.read !== 1 && notification.read !== "1"
  ).length;
  const list = sliceList(notifications);
  const unreadSelectedCount = notifications.filter(
    (notification) =>
      selectedNotificationIds.includes(String(notification.id)) &&
      notification.read !== true &&
      notification.read !== 1 &&
      notification.read !== "1"
  ).length;
  const hero = renderHero(notifications.length, unreadCount);

  let renderContent: React.ReactNode;
  if (!notificationsLoaded) {
    renderContent = (
      <>
        {hero}
        <section className="NotificationPanel">
          <Loader />
        </section>
      </>
    );
  } else if (list.length === 0) {
    renderContent = (
      <>
        {hero}
        <section className="NotificationPanel NotificationPanel--empty">
          <p className="ListMessage">{t("notifications.no_data")}</p>
        </section>
      </>
    );
  } else {
    renderContent = (
      <>
        {hero}
        <section className="NotificationPanel">
          <div className="NotificationPanel-actions">
            <Button
              variant="danger"
              className="Button NotificationPanel-action"
              onClick={handleDeleteSelected}
              disabled={selectedNotificationIds.length === 0}
            >
              {t("notifications.delete")}
            </Button>
            <Button
              className="Button NotificationPanel-action"
              onClick={handleMarkSelectedAsRead}
              disabled={unreadSelectedCount === 0}
            >
              {t("notifications.mark_all_as_read")}
            </Button>
          </div>
          <NotificationListAny
            notifications={list}
            onRead={(notificationId: number | string) =>
              user_id != null ? markNotificationAsRead(notificationId) : undefined
            }
            onDelete={(notificationId: number | string) =>
              user_id != null
                ? deleteNotification(notificationId).then(() =>
                    setSelectedNotificationIds((current) =>
                      current.filter((id) => id !== String(notificationId))
                    )
                  )
                : undefined
            }
            onOpen={handleOpenNotification}
            selectedNotificationIds={selectedNotificationIds}
            onSelectionChange={handleSelectionChange}
          />
          <ListPagerAny
            page={page}
            pageNumber={pageNumber}
            pageSize={pageSize}
            changePage={handleChangePage}
            changePageSize={handleChangePageSize}
          />
        </section>
      </>
    );
  }

  return (
    <div className="Page NotificationPage">
      <div className="Page-content NotificationPage-content">{renderContent}</div>
    </div>
  );
}

function withNotificationData(
  Component: React.ComponentType<NotificationPageProps>
): React.ComponentType<NotificationPageOwnProps & AuthInjectedProps> {
  return function NotificationQueryData(props) {
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(5);
    const userId =
      props.customerData && "id" in props.customerData ? props.customerData.id || null : null;
    const notificationsQuery = useNotifications(userId);
    const markAsRead = useMarkNotificationAsRead(userId);
    const deleteNotification = useDeleteNotification(userId);
    const markManyAsRead = useMarkNotificationsAsRead(userId);
    const deleteMany = useDeleteNotifications(userId);

    return (
      <Component
        {...props}
        user_id={userId}
        notifications={notificationsQuery.notifications as NotificationCardItem[]}
        notificationsLoaded={notificationsQuery.notificationsLoaded}
        page={page}
        pageSize={pageSize}
        changePage={setPage}
        changePageSize={setPageSize}
        markNotificationAsRead={(notificationId) =>
          markAsRead.mutateAsync(notificationId).then((result) => result || null)
        }
        deleteNotification={(notificationId) =>
          deleteNotification.mutateAsync(notificationId).then((result) => result || null)
        }
        markNotificationsAsRead={(notificationIds) => markManyAsRead.mutateAsync(notificationIds)}
        deleteNotifications={(notificationIds) => deleteMany.mutateAsync(notificationIds)}
      />
    );
  };
}

export default withTranslation()(withAuthData(withNotificationData(NotificationPage)));
